import React, { useState } from 'react';
import PropTypes from 'prop-types';
import readXlsxFile from 'read-excel-file';
import {
  EuiButton,
  EuiCallOut,
  EuiFilePicker,
  EuiPanel,
  EuiProgress,
  EuiText,
} from '@elastic/eui';
import { TranslateLanguage } from './KeywordTranslator';

const HEADER_ALIASES = {
  title: ['标题', '標題', '歌名', '诗歌名称', '詩歌名稱', 'title'],
  link: ['链接', '鏈接', '网址', '網址', 'link', 'url'],
  scripture: ['经文', '經文', 'verselist', 'scripture'],
};

const normalizeText = value => String(value ?? '').trim();
const normalizeHeader = value => normalizeText(value).toLowerCase().replace(/[\s_-]/g, '');

const normalizeYouTubeLink = link => {
  const rawLink = normalizeText(link);

  try {
    const url = new URL(rawLink);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] || '';
    } else if (host.endsWith('youtube.com')) {
      videoId = url.searchParams.get('v') || '';
    }

    return videoId ? `youtube:${videoId}` : rawLink;
  } catch {
    return rawLink;
  }
};

const extractNumericId = id => {
  const match = String(id ?? '').match(/\d+$/);
  return match ? Number(match[0]) : id;
};

const findColumnNumber = (headers, aliases) => {
  const normalizedAliases = new Set(aliases.map(normalizeHeader));
  return headers.find(header => normalizedAliases.has(header.value))?.columnNumber ?? null;
};


const buildSongDocument = (song, verselist) => ({
  id: extractNumericId(song.id),
  doctype: '詩歌',
  title: song.title,
  link: song.link,
  publisher: song.publisher,
  description: song.description,
  language: song.language,
  length: song.length,
  tempo: song.tempo,
  key: song.key,
  tag: song.tag,
  official: song.official,
  verselist,
  viewct: song.viewct === '' ? null : song.viewct,
  date: song.date,
});

const ScriptureExcelUpdater = ({ songs, onSongsUpdated }) => {
  const browserLanguage = (navigator.language || navigator.userLanguage) === 'zh-CN' ? 'S' : 'T';
  const language = localStorage.userLanguage || browserLanguage;
  const [filePickerKey, setFilePickerKey] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [message, setMessage] = useState('');

  const translate = text => TranslateLanguage(language, text);

  const analyzeFile = async files => {
    const file = files?.[0];
    setAnalysis(null);
    setMessage('');
    if (!file) return;

    setIsParsing(true);

    try {
      // Some valid XLSX writers emit empty string cells without a text node.
      // Disable the parser's unsafe internal trim; normalizeText() trims safely below.
      const rows = await readXlsxFile(file, { trim: false });
      if (!rows.length) throw new Error('Excel 檔案中沒有可讀取的內容。');

      let headerRowIndex = null;
      let columns = null;

      for (let rowIndex = 0; rowIndex < Math.min(10, rows.length); rowIndex++) {
        const headers = rows[rowIndex].map((value, columnNumber) => ({
          columnNumber,
          value: normalizeHeader(value),
        }));

        const candidate = {
          title: findColumnNumber(headers, HEADER_ALIASES.title),
          link: findColumnNumber(headers, HEADER_ALIASES.link),
          scripture: findColumnNumber(headers, HEADER_ALIASES.scripture),
        };

        if (candidate.title !== null && candidate.link !== null && candidate.scripture !== null) {
          headerRowIndex = rowIndex;
          columns = candidate;
          break;
        }
      }

      if (headerRowIndex === null) {
        const firstTitle = normalizeText(rows[0]?.[0]);
        const firstLink = normalizeText(rows[0]?.[2]);

        if (firstTitle && /^https?:\/\//i.test(firstLink)) {
          headerRowIndex = -1;
          columns = { title: 0, link: 2, scripture: 14 };
        } else {
          throw new Error('找不到「標題、連結、經文」三欄，請確認 Excel 表頭。');
        }
      }

      const exactSongMap = new Map();
      const normalizedSongMap = new Map();
      songs.forEach(song => {
        exactSongMap.set(`${normalizeText(song.title)}\u0000${normalizeText(song.link)}`, song);
        normalizedSongMap.set(`${normalizeText(song.title)}\u0000${normalizeYouTubeLink(song.link)}`, song);
      });

      const changeMap = new Map();
      const unmatched = [];
      const conflicts = [];
      let emptyScriptures = 0;
      let unchanged = 0;

      for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        const rowNumber = rowIndex + 1;
        const title = normalizeText(row?.[columns.title]);
        const link = normalizeText(row?.[columns.link]);
        const nextScripture = normalizeText(row?.[columns.scripture]);

        if (!title && !link && !nextScripture) continue;
        if (!title || !link) {
          unmatched.push({ rowNumber, title: title || '(缺少標題)', link: link || '(缺少連結)' });
          continue;
        }
        if (!nextScripture) {
          emptyScriptures++;
          continue;
        }

        const song = exactSongMap.get(`${title}\u0000${link}`)
          || normalizedSongMap.get(`${title}\u0000${normalizeYouTubeLink(link)}`);

        if (!song) {
          unmatched.push({ rowNumber, title, link });
          continue;
        }

        const currentScripture = normalizeText(song.verselist);
        if (currentScripture === nextScripture) {
          unchanged++;
          continue;
        }

        const songId = String(song.id);
        const existing = changeMap.get(songId);
        if (existing && existing.nextScripture !== nextScripture) {
          conflicts.push({ rowNumber, title, link });
          continue;
        }

        changeMap.set(songId, {
          song,
          rowNumber,
          title,
          link,
          currentScripture,
          nextScripture,
        });
      }

      const changes = Array.from(changeMap.values());
      setAnalysis({
        fileName: file.name,
        sheetName: '第一個工作表',
        changes,
        unmatched,
        conflicts,
        emptyScriptures,
        unchanged,
      });
      setMessage(changes.length
        ? `已識別 ${changes.length} 筆待更新經文，請預覽後提交。`
        : '沒有發現需要更新的非空經文。');
    } catch (error) {
      console.error('讀取經文 Excel 失敗:', error);
      setMessage(`讀取失敗：${error.message}`);
    } finally {
      setIsParsing(false);
      setFilePickerKey(previous => previous + 1);
    }
  };

  const submitUpdates = async () => {
    const changes = analysis?.changes || [];
    if (!changes.length || isSubmitting) return;
    if (!window.confirm(translate(`確認把 ${changes.length} 首詩歌的經文更新提交到伺服器嗎？`))) return;

    const postURL = process.env.REACT_APP_SongDocURL;
    const postKey = process.env.REACT_APP_PostKey;
    const batchSize = 50;
    const totalBatches = Math.ceil(changes.length / batchSize);
    const successfulIds = new Set();
    const failures = [];

    setIsSubmitting(true);
    setProgress({ completed: 0, total: totalBatches });
    setMessage('正在提交經文更新...');

    for (let index = 0; index < changes.length; index += batchSize) {
      const batch = changes.slice(index, index + batchSize);

      try {
        const response = await fetch(postURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': postKey,
          },
          body: JSON.stringify(batch.map(change => buildSongDocument(change.song, change.nextScripture))),
        });

        if (!response.ok) {
          const details = await response.text();
          throw new Error(`${response.status} ${response.statusText}: ${details}`);
        }

        const results = await response.json();
        batch.forEach((change, batchIndex) => {
          const result = Array.isArray(results) ? results[batchIndex] : null;
          if (result?.errors?.length) {
            failures.push(`${change.title}: ${result.errors.join(', ')}`);
          } else {
            successfulIds.add(String(change.song.id));
          }
        });
      } catch (error) {
        console.error('批次提交經文失敗:', error);
        batch.forEach(change => failures.push(`${change.title}: ${error.message}`));
      }

      setProgress({ completed: Math.floor(index / batchSize) + 1, total: totalBatches });
    }

    const updatedScriptures = new Map(
      changes
        .filter(change => successfulIds.has(String(change.song.id)))
        .map(change => [String(change.song.id), change.nextScripture])
    );

    onSongsUpdated(updatedScriptures);
    setAnalysis(previous => previous
      ? { ...previous, changes: previous.changes.filter(change => !successfulIds.has(String(change.song.id))) }
      : previous);
    setMessage(failures.length
      ? `成功 ${successfulIds.size} 筆，失敗 ${failures.length} 筆。第一個錯誤：${failures[0]}`
      : `已成功更新 ${successfulIds.size} 首詩歌的經文。`);
    setIsSubmitting(false);
  };


  return (
    <EuiPanel paddingSize="m" style={{ marginTop: 16, textAlign: 'left' }}>
      <h3 style={{ marginTop: 0 }}>{translate('從 Excel 批次更新經文')}</h3>
      <EuiText size="s">
        <p>{translate('選擇包含「標題、連結、經文」欄的 .xlsx 檔案。系統只提交與伺服器目前值不同的非空經文，提交前會要求確認。')}</p>
      </EuiText>
      <EuiFilePicker
        key={filePickerKey}
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={analyzeFile}
        disabled={isParsing || isSubmitting}
        initialPromptText={translate('選擇或拖放 .xlsx 檔案')}
        display="default"
        fullWidth
        style={{ margin: '8px 0 12px' }}
      />

      {isParsing && <EuiProgress size="xs" color="primary" />}
      {message && (
        <EuiCallOut
          title={translate(message)}
          color={message.startsWith('讀取失敗') || message.includes('失敗 ') ? 'danger' : 'primary'}
          size="s"
          style={{ marginTop: 8 }}
        />
      )}

      {analysis && (
        <div style={{ marginTop: 12 }}>
          <EuiText size="s">
            <p>
              {translate('檔案')}：{analysis.fileName}；{translate('工作表')}：{translate(analysis.sheetName)}；
              {translate('待更新')}：{analysis.changes.length}；{translate('未匹配')}：{analysis.unmatched.length}；
              {translate('衝突')}：{analysis.conflicts.length}；{translate('經文未變')}：{analysis.unchanged}；
              {translate('空經文已忽略')}：{analysis.emptyScriptures}。
            </p>
          </EuiText>

          {analysis.changes.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Excel 行', '詩歌', '目前經文', '更新後經文'].map(label => (
                      <th key={label} style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #d3dae6' }}>{translate(label)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analysis.changes.map(change => (
                    <tr key={`${change.song.id}-${change.rowNumber}`}>
                      <td style={{ padding: 6, verticalAlign: 'top' }}>{change.rowNumber}</td>
                      <td style={{ padding: 6, verticalAlign: 'top', minWidth: 180 }}>{change.title}</td>
                      <td style={{ padding: 6, verticalAlign: 'top', minWidth: 220 }}>{change.currentScripture || translate('(空)')}</td>
                      <td style={{ padding: 6, verticalAlign: 'top', minWidth: 220, background: '#fff2cc' }}>{change.nextScripture}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

            </div>
          )}

          <EuiButton
            fill
            onClick={submitUpdates}
            isLoading={isSubmitting}
            isDisabled={!analysis.changes.length || analysis.conflicts.length > 0}
          >
            {translate(`確認並提交 ${analysis.changes.length} 筆經文更新`)}
          </EuiButton>

          {isSubmitting && progress.total > 0 && (
            <div style={{ marginTop: 10 }}>
              <EuiProgress value={progress.completed} max={progress.total} size="m" color="primary" />
              <EuiText size="xs"><p>{translate('已完成批次')}：{progress.completed}/{progress.total}</p></EuiText>
            </div>
          )}
        </div>
      )}
    </EuiPanel>
  );
};

ScriptureExcelUpdater.propTypes = {
  songs: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSongsUpdated: PropTypes.func.isRequired,
};

export default ScriptureExcelUpdater;
